import { useEffect, useState, useCallback } from "react";
import { api, formatApiError, openSecureFile, markSectionSeen } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { PhotoUploader } from "../components/PhotoUploader";
import { DocumentUploader } from "../components/DocumentUploader";
import { AuthImage } from "../components/AuthImage";
import { SinistroChat } from "../components/SinistroChat";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { ShieldAlert, Building2, FileText, Clock, Send, Paperclip, CheckCircle2, ShieldCheck, User, Save } from "lucide-react";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";

// Iter122cv · Timeline 5 fasi (read-only) per il condòmino
const FASI = ["Bozza-Denuncia", "Aperto-Istruttoria", "Aperto-Valutazione", "Aperto-Liquidazione", "Chiuso-Liquidato"];
const FASE_LABEL = {
  "Bozza-Denuncia": "Apertura",
  "Aperto-Istruttoria": "Denuncia inviata",
  "Aperto-Valutazione": "Valutazione",
  "Aperto-Liquidazione": "Liquidazione",
  "Chiuso-Liquidato": "Chiusa",
};
const FASE_BADGE = {
  "Bozza-Denuncia": "bg-slate-100 text-slate-700",
  "Aperto-Istruttoria": "bg-blue-100 text-blue-800",
  "Aperto-Valutazione": "bg-amber-100 text-amber-800",
  "Aperto-Liquidazione": "bg-purple-100 text-purple-800",
  "Chiuso-Liquidato": "bg-green-100 text-green-800",
};
const fmtData = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" }); } catch { return ""; } };

function FaseTimeline({ fase, timestamps = {} }) {
  const idx = Math.max(0, FASI.indexOf(fase));
  return (
    <div className="rounded-lg border-2 border-slate-200 p-3" data-testid="condomino-fase-timeline">
      <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1">
        <Clock className="w-4 h-4" /> Avanzamento della pratica
      </div>
      <div className="flex flex-wrap gap-1.5">
        {FASI.map((f, i) => {
          const done = i < idx;
          const current = i === idx;
          const ts = timestamps?.[f];
          const cls = current
            ? "border-brand bg-[#FBEEF3] text-brand"
            : done
              ? "border-emerald-400 bg-emerald-50 text-emerald-800"
              : "border-slate-200 bg-white text-slate-400";
          return (
            <div key={f} data-testid={`condomino-fase-step-${f}`}
              className={`flex-1 min-w-[95px] rounded-lg border-2 px-2 py-1.5 flex flex-col items-center justify-center text-center ${cls}`}>
              <span className="flex items-center gap-1 font-bold text-xs sm:text-sm">
                {(done || (current && f === "Chiuso-Liquidato")) && <CheckCircle2 className="w-3.5 h-3.5" />}
                {i + 1}. {FASE_LABEL[f]}
              </span>
              {ts && <span className="text-[10px] text-slate-500">{fmtData(ts)}</span>}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-slate-500 mt-2">
        {fase === "Chiuso-Liquidato"
          ? "La pratica è stata chiusa. Trovi l'esito nella chat o nelle comunicazioni dello Studio."
          : `La pratica è in fase «${FASE_LABEL[fase] || fase}». Lo Studio ti aggiornerà a ogni avanzamento.`}
      </p>
    </div>
  );
}


// Iter122k97 · Card polizza privata del condòmino danneggiato/danneggiante
function PolizzaPrivataCard({ sinistro, onUpdated }) {
  const { user } = useAuth();
  // Trova il record del condòmino tra danneggiati/danneggianti (match _rubrica_id o email)
  const mySubject = (() => {
    const email = (user?.email || "").toLowerCase();
    const sid = user?.soggetto_id;
    for (const [key, list] of [["danneggiati", sinistro.danneggiati || []], ["danneggianti", sinistro.danneggianti || []]]) {
      for (let i = 0; i < list.length; i++) {
        const d = list[i];
        if (sid && d._rubrica_id === sid) return { kind: key, idx: i, ...d };
        if (email && (d.email || "").toLowerCase() === email) return { kind: key, idx: i, ...d };
      }
    }
    return null;
  })();

  const [f, setF] = useState({
    polizza_privata_numero: "",
    polizza_privata_compagnia_nome: "",
    polizza_privata_compagnia_contatti: "",
    polizza_privata_perito_nome: "",
    polizza_privata_perito_contatti: "",
  });
  const [busy, setBusy] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  useEffect(() => {
    if (mySubject) {
      setF({
        polizza_privata_numero: mySubject.polizza_privata_numero || "",
        polizza_privata_compagnia_nome: mySubject.polizza_privata_compagnia_nome || "",
        polizza_privata_compagnia_contatti: mySubject.polizza_privata_compagnia_contatti || "",
        polizza_privata_perito_nome: mySubject.polizza_privata_perito_nome || "",
        polizza_privata_perito_contatti: mySubject.polizza_privata_perito_contatti || "",
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mySubject?.kind, mySubject?.idx, sinistro.id]);

  if (!mySubject) return null;
  // Iter122k103 · Se lo Studio non ha attivato il flag `has_polizza_privata`, la card
  // resta nascosta al condòmino (di default non ne ha una).
  if (!mySubject.has_polizza_privata) return null;
  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); setSavedOk(false); };

  const salva = async () => {
    setBusy(true);
    try {
      await api.put(`/sinistri/${sinistro.id}/polizza-privata`, f);
      // Aggiorna localmente il record del soggetto
      const list = [...(sinistro[mySubject.kind] || [])];
      list[mySubject.idx] = { ...list[mySubject.idx], ...f };
      onUpdated({ [mySubject.kind]: list });
      setSavedOk(true);
      toast.success("Dati polizza privata salvati");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border-2 border-sky-200 bg-sky-50/40 p-3 space-y-2"
      data-testid="condomino-polizza-privata-card">
      <div className="text-sm font-bold uppercase tracking-wide text-sky-800 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4" /> La mia compagnia privata
      </div>
      <p className="text-xs text-slate-600">
        Se hai una polizza personale (RC capofamiglia, RC vita, ecc.) che potrebbe
        intervenire su questo sinistro, compila qui i dati. Lo Studio userà queste
        informazioni per interfacciarsi con la tua compagnia/perito.
      </p>
      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <Label className="text-xs font-semibold">N. sinistro polizza privata</Label>
          <Input data-testid="condomino-polprv-numero" value={f.polizza_privata_numero}
            onChange={(e) => set("polizza_privata_numero", e.target.value)} className="mt-1 h-10 bg-white" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Compagnia (denominazione)</Label>
          <Input data-testid="condomino-polprv-compagnia" value={f.polizza_privata_compagnia_nome}
            onChange={(e) => set("polizza_privata_compagnia_nome", e.target.value)} className="mt-1 h-10 bg-white" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs font-semibold">Compagnia · contatti (tel/email)</Label>
          <Input data-testid="condomino-polprv-compagnia-contatti" value={f.polizza_privata_compagnia_contatti}
            onChange={(e) => set("polizza_privata_compagnia_contatti", e.target.value)} className="mt-1 h-10 bg-white" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Perito (nominativo)</Label>
          <Input data-testid="condomino-polprv-perito" value={f.polizza_privata_perito_nome}
            onChange={(e) => set("polizza_privata_perito_nome", e.target.value)} className="mt-1 h-10 bg-white" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Perito · contatti (tel/email)</Label>
          <Input data-testid="condomino-polprv-perito-contatti" value={f.polizza_privata_perito_contatti}
            onChange={(e) => set("polizza_privata_perito_contatti", e.target.value)} className="mt-1 h-10 bg-white" />
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 pt-1">
        <span className="text-xs text-slate-500 inline-flex items-center gap-1">
          <User className="w-3.5 h-3.5" /> Sei registrato come <b>{mySubject.kind === "danneggiati" ? "danneggiato" : "danneggiante"}</b>
        </span>
        <Button size="sm" onClick={salva} disabled={busy}
          data-testid="condomino-polprv-salva" className="h-9 bg-sky-700 hover:bg-sky-800 text-white">
          {savedOk ? <><CheckCircle2 className="w-4 h-4 mr-1" /> Salvato</> : <><Save className="w-4 h-4 mr-1" /> {busy ? "Salvo…" : "Salva dati"}</>}
        </Button>
      </div>
    </div>
  );
}


export default function CondominoSinistri() {
  const [sinistri, setSinistri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [testo, setTesto] = useState("");
  const [foto, setFoto] = useState([]);
  const [allegati, setAllegati] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/sinistri").then((r) => setSinistri(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
    // Iter122at · azzera badge "sinistri" nel menu condòmino
    markSectionSeen("sinistri");
  }, [load]);

  const openDetail = async (s) => {
    setTesto(""); setFoto([]); setAllegati([]);
    try {
      const { data } = await api.get(`/sinistri/${s.id}`);
      setSel(data);
    } catch { setSel(s); }
    setOpen(true);
    // Iter122ig · marca la pratica come vista e azzera i badge locali
    try {
      await api.post(`/sinistri/${s.id}/visto`);
      setSinistri((prev) => prev.map((x) => x.id === s.id ? { ...x, messaggi_non_letti: 0, doc_nuovi: 0 } : x));
    } catch (_e) { /* silenzioso */ }
  };

  const invia = async () => {
    setBusy(true);
    try {
      await api.post(`/sinistri/${sel.id}/documentazione`, { testo, foto, allegati });
      toast.success("Documentazione inviata allo Studio. Grazie!");
      const { data } = await api.get(`/sinistri/${sel.id}`);
      setSel(data); setTesto(""); setFoto([]); setAllegati([]);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="condomino-sinistri-page">
      <div className="flex items-center gap-3 mb-5">
        <div className="bg-brand text-white rounded-xl p-2.5"><ShieldAlert className="w-6 h-6" /></div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">I miei sinistri</h1>
          <p className="text-base text-slate-500">Sinistri collegati alle tue manutenzioni. Puoi integrare foto, preventivi, fatture e note.</p>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500">Caricamento...</div>
      ) : sinistri.length === 0 ? (
        <Card className="p-6 text-center text-slate-500" data-testid="no-sinistri">
          Non risultano sinistri collegati alle tue manutenzioni.
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {sinistri.map((s) => (
            <Card key={s.id} data-testid={`sinistro-card-${s.numero_pratica}`} className="p-4 border-2 cursor-pointer hover:border-[#C98AA0] relative" onClick={() => openDetail(s)}>
              {/* Iter122ig · Badge rosso per messaggi/documenti non letti dallo Studio */}
              {((s.messaggi_non_letti || 0) + (s.doc_nuovi || 0)) > 0 && (
                <span data-testid={`sinistro-unread-${s.numero_pratica}`}
                  className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[26px] h-[26px] px-2 rounded-full bg-red-600 text-white text-xs font-extrabold shadow-md ring-2 ring-white animate-pulse"
                  title={`${s.messaggi_non_letti || 0} messaggi · ${s.doc_nuovi || 0} nuovi documenti`}>
                  {(s.messaggi_non_letti || 0) + (s.doc_nuovi || 0)}
                </span>
              )}
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-brand">{s.numero_pratica}</span>
                <span data-testid={`fase-badge-${s.numero_pratica}`} className={`text-xs px-2 py-0.5 rounded-full font-semibold ${FASE_BADGE[s.fase] || "bg-slate-100"}`}>{FASE_LABEL[s.fase] || s.fase}</span>
              </div>
              <div className="text-sm text-slate-600 flex items-center gap-1 mb-1"><Building2 className="w-4 h-4" /> {s.condominio_nome}</div>
              {s.ticket_numero && <div className="text-sm text-slate-500 flex items-center gap-1 mb-1"><FileText className="w-4 h-4" /> Manutenzione {s.ticket_numero}</div>}
              <p className="text-base text-slate-800 line-clamp-2">{s.descrizione || "—"}</p>
              {((s.messaggi_non_letti || 0) + (s.doc_nuovi || 0)) > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5 text-xs" data-testid={`sinistro-unread-detail-${s.numero_pratica}`}>
                  {(s.messaggi_non_letti || 0) > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-bold">
                      {s.messaggi_non_letti} nuovo{s.messaggi_non_letti > 1 ? "i" : ""} messaggio{s.messaggi_non_letti > 1 ? "" : ""}
                    </span>
                  )}
                  {(s.doc_nuovi || 0) > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-bold">
                      {s.doc_nuovi} nuovo{s.doc_nuovi > 1 ? "i" : ""} documento/foto
                    </span>
                  )}
                </div>
              )}
              <div className="mt-2 text-sm font-semibold text-brand flex items-center gap-1"><Paperclip className="w-4 h-4" /> Apri e integra documentazione</div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{sel?.numero_pratica} — {sel?.condominio_nome}</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-4">
              {/* Iter122cv · Timeline 5 fasi read-only */}
              <FaseTimeline fase={sel.fase} timestamps={sel.fasi_timestamps} />

              <div className="rounded-lg border-2 border-slate-200 p-3">
                <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-1">Descrizione del sinistro</div>
                <p className="text-base text-slate-800 whitespace-pre-wrap">{sel.descrizione || "—"}</p>
                <div className="text-sm text-slate-500 mt-2">Fase: <strong>{FASE_LABEL[sel.fase] || sel.fase}</strong>{sel.compagnia_nome ? ` · Compagnia: ${sel.compagnia_nome}` : ""}</div>
              </div>

              {/* Iter122ic · Cronologia attività (voci flaggate visibile_cliente) */}
              <CronologiaBlock history={sel.history || []} />

              {/* Iter122k97 · Card polizza privata del condòmino (se listato come danneggiato/danneggiante) */}
              <PolizzaPrivataCard sinistro={sel} onUpdated={(patch) => setSel((p) => ({ ...p, ...patch }))} />

              {(sel.documentazione_condomino || []).length > 0 && (
                <div data-testid="documentazione-list">
                  <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-2">Documentazione inviata</div>
                  <div className="space-y-2">
                    {sel.documentazione_condomino.map((d) => (
                      <div key={d.id} className="rounded-lg border-2 border-slate-100 bg-slate-50 p-3">
                        <div className="text-xs text-slate-500 mb-1">{d.author_nome} · {new Date(d.created_at).toLocaleDateString("it-IT")}</div>
                        {d.testo && <p className="text-base text-slate-800 whitespace-pre-wrap mb-2">{d.testo}</p>}
                        <div className="flex flex-wrap gap-2">
                          {(d.foto || []).map((f) => (
                            <div key={f.id} className="w-16 h-16 rounded overflow-hidden border"><AuthImage fileId={f.id} className="w-full h-full object-cover" /></div>
                          ))}
                          {(d.allegati || []).map((f) => (
                            <button type="button" key={f.id} onClick={() => openSecureFile(`/files/${f.id}`)} className="inline-flex items-center gap-1 px-2 py-1 rounded border text-brand text-sm font-semibold">
                              <FileText className="w-4 h-4" /> {f.filename || "Documento"}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-lg border-2 border-[#EBC5D2] bg-[#FBEEF3]/40 p-3">
                <div className="text-sm font-bold uppercase tracking-wide text-brand mb-2">Aggiungi documentazione</div>
                <Label className="text-sm font-semibold">Note / descrizione</Label>
                <Textarea data-testid="documentazione-testo" value={testo} onChange={(e) => setTesto(e.target.value)} className="mt-1" placeholder="Es. descrizione dei danni, riferimenti, ecc." />
                <div className="mt-3">
                  <Label className="text-sm font-semibold">Foto</Label>
                  <div className="mt-1"><PhotoUploader photos={foto} onChange={setFoto} testid="documentazione-foto" /></div>
                </div>
                <div className="mt-3">
                  <Label className="text-sm font-semibold">Preventivi / fatture / documenti</Label>
                  <div className="mt-1"><DocumentUploader docs={allegati} onChange={setAllegati} testid="documentazione-doc" /></div>
                </div>
              </div>

              {/* Iter122cu · Chat Studio ↔ Condòmino (canale dedicato) */}
              <SinistroChat sinistroId={sel.id} messaggi={sel.messaggi || []} me="Condòmino" kind="condomino"
                title="Chat con lo Studio"
                onNew={(m) => setSel((prev) => ({ ...prev, messaggi: [...(prev.messaggi || []), m] }))} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="h-12">Chiudi</Button>
            <Button data-testid="invia-documentazione-button" onClick={invia} disabled={busy} className="h-12 bg-brand hover:bg-brand-dark">
              <Send className="w-4 h-4 mr-1.5" /> {busy ? "Invio..." : "Invia documentazione"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Iter122ic · Cronologia attività del sinistro visibile al condòmino.
// Filtra le voci con `visibile_cliente !== false`. Vuota se non c'è nulla da mostrare.
function CronologiaBlock({ history }) {
  const visibili = (history || []).filter((h) => h && h.visibile_cliente !== false && (h.action || h.testo));
  if (visibili.length === 0) return null;
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch (_e) { return iso; }
  };
  // ordine cronologico crescente (più vecchia in alto), come un timeline
  const items = [...visibili].sort((a, b) => (a.at || "").localeCompare(b.at || ""));
  return (
    <div data-testid="sinistro-cronologia" className="rounded-lg border-2 border-slate-200 p-3">
      <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-2">
        <Clock className="w-4 h-4" /> Cronologia attività
      </div>
      <ol className="relative border-l-2 border-slate-200 ml-2 space-y-3">
        {items.map((h, i) => (
          <li key={h.id || i} className="pl-4 relative" data-testid={`cron-item-${i}`}>
            <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-brand ring-2 ring-white" />
            <div className="text-xs text-slate-500">{fmtDate(h.at)}{h.by ? ` · ${h.by}` : ""}</div>
            <div className="text-sm text-slate-800 font-semibold break-words">{h.action || h.testo}</div>
            {h.note && <div className="text-sm text-slate-600 whitespace-pre-wrap break-words mt-0.5">{h.note}</div>}
          </li>
        ))}
      </ol>
    </div>
  );
}

